import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  CHART_MEASURES,
  decodeFilters,
  encodeFilters,
  type AnalyticsFilters,
  type ChartMeasure,
  type FlowDateField,
} from '@/services/dashboardAnalytics'

/** URL keys. `cohort` is shared by every cohort-aware panel on a board (the
 *  Explorer's filter chips ARE the board's cohort, T-52), so narrowing in the
 *  Explorer and switching tab keeps the same population. */
export const COHORT_PARAM = 'cohort'
const DIMENSION_PARAM = 'xdim'
const BY_PARAM = 'xby'
const MEASURE_PARAM = 'xm'
const FLOW_PARAM = 'xflow'

/** "No second dimension" — distinct from an absent param, which means the
 *  default (stack by outcome). */
export const BY_NONE = 'none'

export const DEFAULT_DIMENSION = 'underwriter'
export const DEFAULT_BY = 'outcome'
export const DEFAULT_MEASURE: ChartMeasure = 'commitment'

const FLOW_FIELDS: FlowDateField[] = ['event', 'received', 'funded', 'status']

export interface ExplorerState {
  dimension: string
  by: string | null
  measure: ChartMeasure
  flowField: FlowDateField
}

/** Pure read of the Explorer's state from a query string — tested. */
export function readExplorerState(params: URLSearchParams): ExplorerState {
  const dimension = params.get(DIMENSION_PARAM) || DEFAULT_DIMENSION
  const rawBy = params.get(BY_PARAM)
  const by = rawBy === BY_NONE ? null : rawBy || DEFAULT_BY
  const rawMeasure = params.get(MEASURE_PARAM) as ChartMeasure | null
  const measure = rawMeasure && CHART_MEASURES.includes(rawMeasure) ? rawMeasure : DEFAULT_MEASURE
  const rawFlow = params.get(FLOW_PARAM) as FlowDateField | null
  const flowField = rawFlow && FLOW_FIELDS.includes(rawFlow) ? rawFlow : 'event'
  // A "by" equal to the dimension is the same grouping twice.
  return { dimension, by: by === dimension ? null : by, measure, flowField }
}

/** Pure write — returns the next query string, leaving other params alone. */
export function writeExplorerState(
  params: URLSearchParams,
  patch: Partial<ExplorerState>,
): URLSearchParams {
  const next = new URLSearchParams(params)
  if (patch.dimension !== undefined) next.set(DIMENSION_PARAM, patch.dimension)
  if (patch.by !== undefined) next.set(BY_PARAM, patch.by ?? BY_NONE)
  if (patch.measure !== undefined) next.set(MEASURE_PARAM, patch.measure)
  if (patch.flowField !== undefined) next.set(FLOW_PARAM, patch.flowField)
  return next
}

export function useExplorerState(): [ExplorerState, (patch: Partial<ExplorerState>) => void] {
  const [params, setParams] = useSearchParams()
  const state = useMemo(() => readExplorerState(params), [params])
  const update = useCallback(
    (patch: Partial<ExplorerState>) =>
      setParams((prev) => writeExplorerState(prev, patch), { replace: true }),
    [setParams],
  )
  return [state, update]
}

/** The board's cohort (filter set), read from and written to the URL. */
export function useCohort(): [AnalyticsFilters, (next: AnalyticsFilters) => void] {
  const [params, setParams] = useSearchParams()
  const raw = params.get(COHORT_PARAM)
  const cohort = useMemo(() => decodeFilters(raw), [raw])
  const setCohort = useCallback(
    (next: AnalyticsFilters) =>
      setParams(
        (prev) => {
          const out = new URLSearchParams(prev)
          const encoded = encodeFilters(next)
          if (encoded) out.set(COHORT_PARAM, encoded)
          else out.delete(COHORT_PARAM)
          return out
        },
        { replace: true },
      ),
    [setParams],
  )
  return [cohort, setCohort]
}
